import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Send, MapPin, Phone, Mail, Clock } from 'lucide-react';
import logger from '../utils/logger';

const Contact = () => {
  const { t } = useTranslation('public');
  const [contactForm, setContactForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneButton, setPhoneButton] = useState({ enabled: true, phone_number: '01 84 80 87 29' });
  const [contactInfo, setContactInfo] = useState({
    adresse: '92 Rue de la Bonne Aventure',
    code_postal: '78000',
    ville: 'Versailles',
    pays: 'France',
    email: 'gestion@asarcsg.com',
    email_note: 'Nous répondons sous 24h ouvrées',
    telephone: '+33 1 84 80 87 29',
    telephone_note: 'Du lundi au vendredi : 9h - 18h',
    horaires_semaine: 'Lundi - Vendredi : 9h00 - 18h00',
    horaires_samedi: 'Samedi : 9h00 - 12h00',
    horaires_dimanche: 'Dimanche : Fermé',
    orias_numero: '12 345 678',
    acpr_adresse: '4 Place de Budapest, 75009 Paris'
  });
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Charger bouton téléphone
        const phoneResponse = await fetch(`${backendUrl}/api/site-settings/phone-button`);
        if (phoneResponse.ok) {
          const data = await phoneResponse.json();
          setPhoneButton(data);
        }
        // Charger infos contact
        const contactResponse = await fetch(`${backendUrl}/api/site-settings/contact-info`);
        if (contactResponse.ok) {
          const data = await contactResponse.json();
          setContactInfo(data);
        }
      } catch (error) {
        // Utiliser les valeurs par défaut
      }
    };
    loadSettings();
  }, [backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${contactForm.prenom} ${contactForm.nom}`,
          email: contactForm.email,
          phone: contactForm.telephone,
          subject: contactForm.sujet || 'Contact général',
          message: contactForm.message
        })
      });
      
      if (response.ok) {
        alert(t('contact.form.success') || 'Votre message a été envoyé avec succès !');
        setContactForm({ nom: '', prenom: '', email: '', telephone: '', sujet: '', message: '' });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      logger.error('Erreur envoi formulaire contact:', error);
      alert(t('contact.form.error') || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* En-tête */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-brand-text mb-4">{t('contact.title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Informations de contact */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-brand-text mb-6">{t('contact.coordinates')}</h2>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <MapPin className="text-brand-primary mr-4 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.address')}</h3>
                  <p className="text-gray-600">
                    {contactInfo.adresse}<br />
                    {contactInfo.code_postal} {contactInfo.ville}<br />
                    {contactInfo.pays}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Mail className="text-brand-primary mr-4 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.email')}</h3>
                  <p className="text-gray-600">
                    <a href={`mailto:${contactInfo.email}`} className="text-brand-primary hover:underline">
                      {contactInfo.email}
                    </a>
                  </p>
                  {contactInfo.email_note && (
                    <p className="text-sm text-gray-500 mt-1">
                      {contactInfo.email_note}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="text-brand-primary mr-4 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.phone')}</h3>
                  <p className="text-gray-600">
                    <a href={`tel:${contactInfo.telephone.replace(/\s/g, '')}`} className="text-brand-primary hover:underline">
                      {contactInfo.telephone}
                    </a>
                  </p>
                  {contactInfo.telephone_note && (
                    <p className="text-sm text-gray-500 mt-1">
                      {contactInfo.telephone_note}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start">
                <Clock className="text-brand-primary mr-4 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.hours')}</h3>
                  <div className="text-gray-600 space-y-1">
                    <p>{contactInfo.horaires_semaine}</p>
                    <p>{contactInfo.horaires_samedi}</p>
                    <p>{contactInfo.horaires_dimanche}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations professionnelles */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">{t('contact.professionalInfo')}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>{t('contact.brokerTitle')}</strong></p>
                <p>{t('contact.oriasRegistered')} {contactInfo.orias_numero}</p>
                <p>{t('contact.financialGuarantee')}</p>
                <p>{t('contact.acprControlled')} {contactInfo.acpr_adresse}</p>
              </div>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-brand-text mb-6">{t('contact.form.title')}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.lastName')} *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    required
                    value={contactForm.nom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder={t('contact.form.lastName')}
                  />
                </div>
                
                <div>
                  <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.firstName')} *
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    required
                    value={contactForm.prenom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder={t('contact.form.firstName')}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={contactForm.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="votre.email@exemple.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.phone')} *
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    required
                    value={contactForm.telephone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="01 84 80 87 29"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.subject')}
                </label>
                <select
                  id="sujet"
                  name="sujet"
                  value={contactForm.sujet}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                >
                  <option value="">{t('contact.form.selectSubject')}</option>
                  <option value="devis">{t('contact.form.subjects.quote')}</option>
                  <option value="particulier">{t('contact.form.subjects.individual')}</option>
                  <option value="professionnel">{t('contact.form.subjects.professional')}</option>
                  <option value="expatrie">{t('contact.form.subjects.expatriate')}</option>
                  <option value="voyage">{t('contact.form.subjects.travel')}</option>
                  <option value="sinistre">{t('contact.form.subjects.claim')}</option>
                  <option value="modification">{t('contact.form.subjects.modification')}</option>
                  <option value="resiliation">{t('contact.form.subjects.cancellation')}</option>
                  <option value="autre">{t('contact.form.subjects.other')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.message')} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="6"
                  value={contactForm.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                  placeholder={t('contact.form.messagePlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 flex items-center justify-center ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-brand-primary hover:bg-brand-primary-hover hover:shadow-lg'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t('contact.form.sending')}
                  </>
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    {t('contact.form.send')}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-brand-primary">
              <p className="text-sm text-blue-800">
                <strong>{t('contact.form.dataProtection')}</strong> {t('contact.form.dataProtectionText')} 
                <a href="/rgpd" className="underline hover:text-blue-900"> {t('contact.form.privacyLink')}</a>.
              </p>
            </div>
          </div>
        </div>

        {/* Section CTA */}
        <div className="mt-12 bg-gradient-to-r from-brand-primary to-brand-primary-hover rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">{t('contact.ctaTitle')}</h2>
          <p className="text-lg mb-6 opacity-90">
            {t('contact.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {phoneButton.enabled && (
              <a
                href={`tel:${phoneButton.phone_number.replace(/\s/g, '')}`}
                className="bg-white text-brand-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
              >
                <Phone className="mr-2" size={18} />
                {phoneButton.phone_number}
              </a>
            )}
            <Link
              to="/rendez-vous"
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-brand-primary transition-colors"
            >
              {t('contact.ctaAppointment')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;